import { Fragment, useEffect, useState } from "react";
import { useAuth } from "../../../../auth/AuthenticationWrapper";
import CustomButton from "../../../../components/lib/customButton";
import CustomerService from "../../../../rest/customerService";
import InfoField from "../../../../components/lib/infoField";
import ConfirmDeleteModal from "../../../../components/reusables/confirmDeleteModal";
import { toast } from "react-toastify";
import ParentEditStepper from "./parentEditStepper";
import ConfirmCloseView from "../confirmCloseView";
import Modal from "../../../../components/lib/modal";
import { useParentLiveUpdate } from "../../hooks/parent/useParentLiveUpdate";
import { Transition } from "@headlessui/react";

const StudentsList = ({ students }) => {
    return (
        <div className="flex flex-col gap-2">
            <p className="text-sm font-semibold text-custom_gray_font">
                Alunos vinculados
            </p>

            <div className="w-full max-h-96 overflow-y-auto rounded-lg p-2 space-y-2">
                {(!students || students.length === 0) && (
                    <p className="text-sm text-center text-gray-400 py-4">
                        Nenhum aluno vinculado
                    </p>
                )}

                {students?.map((s, index) => (
                    <div
                        key={index}
                        className="bg-white rounded-md border px-3 py-2 grid grid-cols-[2fr_1fr] items-center gap-2 hover:bg-gray-50 transition"
                    >
                        <div className="flex flex-col overflow-hidden">
                            <span className="text-sm font-medium truncate">
                                {s.name}
                            </span>
                            <span className="text-xs text-gray-400 truncate">
                                {s.login}
                            </span>
                        </div>

                        <span className="text-sm text-right font-medium truncate">
                            {s.grade}
                        </span>
                    </div>
                ))}
            </div>
        </div>
    );
};

const ParentDetailsModalContent = ({ client, onEdit, onDelete }) => {
    const liveClient = useParentLiveUpdate(client);

    const { hasPermission } = useAuth();
    const canEdit = hasPermission("customers", "update");
    const canDelete = hasPermission("customers", "delete");

    const [students, setStudents] = useState([]);
    const [showModalDelete, setShowModalDelete] = useState(false);
    const [showEditModal, setShowEditModal] = useState(false);

    const [hasData, setHasData] = useState(false);
    const [confirmClose, setConfirmClose] = useState(false);

    const requestClose = () => {
        if (hasData) {
            setConfirmClose(true);
        } else {
            setShowEditModal(false);
        }
    };

    const forceClose = () => {
        setConfirmClose(false);
        setShowEditModal(false);
    };

    useEffect(() => {
        const fetchData = async () => {
            if (!liveClient?.login) return;
            try {
                const data = await CustomerService.findStudentsByParent(liveClient.login);
                setStudents(data);
            } catch (error) {
                console.error(error)
            }
        }
        fetchData();
    }, [liveClient]);

    const handleDelete = async () => {
        try {
            await CustomerService.deleteParent(liveClient.login);
            toast.success("Responsável removido com sucesso!");
            setShowModalDelete(false);

            if (onDelete) onDelete();
        } catch (error) {
            console.error(error);
            toast.error("Erro ao remover responsável.");
        }
    };

    const handleEditSubmit = async (updatedData) => {
        try {
            await CustomerService.updateParent(liveClient.login, updatedData);
            toast.success('Responsável atualizado com sucesso!');
            setShowEditModal(false);

            return Promise.resolve();
        } catch (error) {
            console.error(error);
            toast.error('Erro ao atualizar responsável');
            return Promise.reject(error);
        }
    };

    return (
        <Transition
            appear
            show={true}
            as={Fragment}
            enter="transition ease-out duration-300"
            enterFrom="opacity-0 scale-95 translate-y-4"
            enterTo="opacity-100 scale-100 translate-y-0"
        >
            <div className="flex flex-col h-[75vh] md:h-[650px] max-h-[80vh]">
                <div className="flex-1 overflow-y-auto px-4 py-2 sm:px-2">
                    <div className="flex flex-col lg:flex-row gap-6">
                        <div className="w-full lg:w-5/12 space-y-4">
                            <div className="grid grid-cols-2 gap-3">
                                <InfoField label="Nome" value={liveClient.name} />
                                <InfoField label="CPF" value={liveClient.cpf} />
                            </div>

                            <InfoField label="Login" value={liveClient.login} />
                            <div className="grid grid-cols-1 gap-3">
                                <InfoField label="Email" value={liveClient.email || "Não registrado"} />
                                <InfoField label="Telefone" value={liveClient.phone_number === '' || liveClient.phone_number === null ? "Não registrado" : liveClient.phone_number} />
                            </div>

                            <InfoField label="Endereço" value={liveClient.address === '' || liveClient.address === null ? "Não registrado" : liveClient.address} />
                        </div>

                        <div className="w-full lg:w-7/12 flex flex-col gap-4">
                            <StudentsList students={students} />
                        </div>

                    </div>
                </div>

                <div className="mt-auto border-t border-gray-200 bg-gray-50 p-3 sm:p-4 rounded-b-lg">
                    <div className="grid grid-cols-2 sm:flex sm:justify-end gap-3">
                        <CustomButton
                            buttonType="edit"
                            text="Editar"
                            onClick={() => setShowEditModal(true)}
                            disabled={!canEdit}
                            className="w-full sm:w-auto text-sm"
                        />
                        <CustomButton
                            buttonType="delete"
                            text="Excluir"
                            onClick={() => setShowModalDelete(true)}
                            disabled={!canDelete}
                            className="w-full sm:w-auto text-sm"
                        />
                    </div>
                </div>

                {showEditModal && (
                    <Modal
                        isOpen={showEditModal}
                        setIsOpen={requestClose}
                        title="Editar Responsável"
                        hideFooter
                        maxWidth="max-w-xl lg:max-w-2xl md:-mr-16"
                    >
                        <div className="relative h-full flex flex-col">
                            <ParentEditStepper
                                parent={liveClient}
                                students={students}
                                isSubmitting={false}
                                onCancel={requestClose}
                                onDirty={() => setHasData(true)}
                                onFinish={async ({ parent }) => {
                                    await handleEditSubmit(parent);
                                }}
                            />
                            {confirmClose && (
                                <div className="absolute inset-0 z-50 flex items-center justify-center bg-white/95 backdrop-blur-sm p-4 rounded-lg">
                                    <ConfirmCloseView
                                        onCancel={() => setConfirmClose(false)}
                                        onConfirm={forceClose}
                                    />
                                </div>
                            )}
                        </div>
                    </Modal>
                )}

                <ConfirmDeleteModal
                    isOpen={showModalDelete}
                    title="Excluir responsável"
                    message={`Tem certeza que deseja excluir ${liveClient.name}?`}
                    warning="Essa ação não poderá ser desfeita."
                    onConfirm={handleDelete}
                    onCancel={() => setShowModalDelete(false)}
                />
            </div>
        </Transition>
    );
};

export default ParentDetailsModalContent;